import {
  content_v1_DatasourcePreset,
  core_v1_DataSource,
} from '@antimatterhq/asl-api-ts-v1';
import { produce, WritableDraft } from 'immer';
import { getTemporaryFields, setTemporaryFields } from './temporaryFields';
import {
  deepEqual,
  getCustomFunctionValues,
  getFieldSchemaValues,
  getJSONExtractValues,
  getMode,
  getOverrideLiquidColumnsValues,
  getPreserveUnreferencedColumnsValues,
  setCustomFunction,
  setFieldSchemaValues,
  setJsonExtract,
  setPreserveUnreferencedColumns,
} from './';
import { SilverDataSourceSchemaValues } from '@/zod/schemas/dataSource/Silver';
import { filterNulls } from '@/utils/filterNulls';
import {
  Datasource_PresetItem,
  Datasource_Silver_AddTable,
  Datasource_Silver_ModifyTable,
  TransformRequest_Transform,
} from '@/types/api';
import {
  AddTableSchemaValues,
  ModifyTableSchemaValues,
} from '@/zod/schemas/dataSource/Common';

export const getModifySilverTables = (
  modifyTables: Datasource_Silver_ModifyTable[],
  omitTables: string[],
): ModifyTableSchemaValues[] => {
  const tables = modifyTables.map((table) => {
    return {
      name: table.name || '',
      jsonExtract: getJSONExtractValues(table.utils || {}),
      ...getPreserveUnreferencedColumnsValues(table.utils || {}),
      ...getCustomFunctionValues(table.custom),
      filter: table.filter || '',
      postFilter: table.postFilter || '',
      omitFields: table.omitFields || [],
      addFields: getFieldSchemaValues(filterNulls(table.addFields)),
      ...getOverrideLiquidColumnsValues(
        filterNulls(table.overrideLiquidColumns),
      ),
      sourceTable: '',
      tableMode: getMode(table.name || '', table.custom, omitTables),
      ...getTemporaryFields(table.utils),
    };
  });

  return tables;
};

export const setModifySilverTables = (
  modifyTablesValues: ModifyTableSchemaValues[],
): Datasource_Silver_ModifyTable[] => {
  return modifyTablesValues.map((table) => {
    const overrideLiquidColumns = table.overrideLiquidColumns.filter(
      (q) => !!q,
    ) as string[];
    const configure = table.tableMode === 'configure';
    const custom = table.tableMode === 'custom';

    const modifyTable: Datasource_Silver_ModifyTable = {
      name: table.name || '',
      utils: configure
        ? {
            jsonExtract: setJsonExtract(table.jsonExtract),
            unreferencedColumns: setPreserveUnreferencedColumns(table),
            temporaryFields: setTemporaryFields(table),
          }
        : undefined,
      custom: custom ? setCustomFunction(table) : undefined,
      filter: configure ? table.filter || undefined : undefined,
      postFilter: configure ? table.postFilter || undefined : undefined,
      omitFields: configure ? table.omitFields || undefined : undefined,
      addFields: configure ? setFieldSchemaValues(table.addFields) : undefined,
      overrideLiquidColumns:
        configure &&
        overrideLiquidColumns.length &&
        table.enableOverrideLiquidColumns
          ? overrideLiquidColumns
          : undefined,
    };

    return modifyTable;
  });
};

export const getAddSilverTables = (
  addTables: Datasource_Silver_AddTable[] | undefined,
): AddTableSchemaValues[] => {
  return (addTables ?? []).map((table) => ({
    name: table.name ?? '',
    jsonExtract: getJSONExtractValues(table.utils ?? {}),
    ...getPreserveUnreferencedColumnsValues(table.utils ?? {}),
    ...getCustomFunctionValues(table.custom),
    filter: table.filter ?? '',
    postFilter: table.postFilter ?? '',
    fields: getFieldSchemaValues(filterNulls(table.fields)),
    ...getOverrideLiquidColumnsValues(filterNulls(table.overrideLiquidColumns)),
    sourceTable: '',
    tableMode: getMode(table.name || '', table.custom, []),
    ...getTemporaryFields(table.utils),
  }));
};

export const setAddSilverTables = (
  addTablesValues: AddTableSchemaValues[],
): NonNullable<Datasource_Silver_AddTable>[] => {
  return addTablesValues.map((table) => {
    const overrideLiquidColumns = table.overrideLiquidColumns.filter(
      (q) => !!q,
    ) as string[];

    const configure = table.tableMode === 'configure';
    const custom = table.tableMode === 'custom';

    const addTable: Datasource_Silver_AddTable = {
      name: table.name,
      utils: configure
        ? {
            jsonExtract: setJsonExtract(table.jsonExtract),
            unreferencedColumns: setPreserveUnreferencedColumns(table),
            temporaryFields: setTemporaryFields(table),
          }
        : undefined,
      custom: custom ? setCustomFunction(table) : undefined,
      filter: configure ? table.filter || undefined : undefined,
      postFilter: configure ? table.postFilter || undefined : undefined,
      fields: configure ? setFieldSchemaValues(table.fields) : undefined,
      overrideLiquidColumns:
        configure &&
        overrideLiquidColumns.length &&
        table.enableOverrideLiquidColumns
          ? overrideLiquidColumns
          : undefined,
    };

    return addTable;
  });
};

export const getSilverInitialValues = (
  dataSource: core_v1_DataSource,
): SilverDataSourceSchemaValues => {
  return {
    skip: dataSource.spec.silver?.transform?.skipSilverTransform ?? false,
    doNotMaterialize:
      dataSource.spec.silver?.transform?.doNotMaterialize ?? false,
    addTables: getAddSilverTables(
      dataSource.spec.silver?.transform?.presetOverrides?.addTables ?? [],
    ),
    modifyTables: getModifySilverTables(
      dataSource.spec.silver?.transform?.presetOverrides?.modifyTables ?? [],
      dataSource.spec.silver?.transform?.presetOverrides?.omitTables ?? [],
    ),
    customNotebookPath: dataSource.spec.custom?.notebook ?? '',
  };
};

export const getIsSilverCompleted = (dataSource: core_v1_DataSource) => {
  return !!dataSource;
};

export const setSilverFormValues = (
  baseState: core_v1_DataSource,
  values: SilverDataSourceSchemaValues,
) => {
  const omitTables = values.modifyTables
    .filter((table) => table.tableMode === 'disable')
    .map((table) => table.name);
  return produce(baseState, (draftState: WritableDraft<core_v1_DataSource>) => {
    draftState.spec.silver = {
      ...draftState.spec.silver,
      transform: {
        skipSilverTransform: values.skip,
        doNotMaterialize: values.doNotMaterialize,
        presetOverrides: {
          addTables: setAddSilverTables(values.addTables),
          modifyTables: setModifySilverTables(values.modifyTables),
          omitTables: omitTables.length ? omitTables : undefined,
        },
      },
    };
    draftState.spec.custom = values.customNotebookPath
      ? {
          notebook: values.customNotebookPath,
        }
      : undefined;
  });
};

const mapSilverModifyTablePresetToDatasource = (
  presetTransform: Datasource_PresetItem,
  datasource: core_v1_DataSource,
) => {
  const modifyTables =
    datasource.spec.silver?.transform?.presetOverrides?.modifyTables;

  const modifyTable = modifyTables?.find(
    (table) => table.name === presetTransform.name,
  );
  if (!modifyTable) {
    return {
      filter: presetTransform.filter || undefined,
      postFilter: presetTransform.postFilter || undefined,
      utils: presetTransform.utils || undefined,
      name: presetTransform.name,
    };
  }

  if (presetTransform.filter) {
    modifyTable.filter = presetTransform.filter;
  }

  if (presetTransform.postFilter) {
    modifyTable.postFilter = presetTransform.postFilter;
  }

  if (!modifyTable.utils) {
    modifyTable.utils = {};
  }

  if (presetTransform.utils?.temporaryFields) {
    modifyTable.utils.temporaryFields = presetTransform.utils.temporaryFields;
  }

  if (!modifyTable.utils.unreferencedColumns) {
    modifyTable.utils.unreferencedColumns = {};
  }

  if (presetTransform.utils?.unreferencedColumns?.preserve !== undefined) {
    modifyTable.utils.unreferencedColumns.preserve =
      presetTransform.utils?.unreferencedColumns?.preserve;
  }

  if (presetTransform.utils?.unreferencedColumns?.embedColumn) {
    modifyTable.utils.unreferencedColumns.embedColumn =
      presetTransform.utils?.unreferencedColumns?.embedColumn;
  }

  if (presetTransform.utils?.unreferencedColumns?.embedColumnType) {
    modifyTable.utils.unreferencedColumns.embedColumnType =
      presetTransform.utils?.unreferencedColumns?.embedColumnType;
  }

  if (presetTransform.utils?.unreferencedColumns?.duplicatePrefix) {
    modifyTable.utils.unreferencedColumns.duplicatePrefix =
      presetTransform.utils?.unreferencedColumns?.duplicatePrefix;
  }

  if (presetTransform.utils?.unreferencedColumns?.omitColumns) {
    modifyTable.utils.unreferencedColumns.omitColumns =
      presetTransform.utils?.unreferencedColumns?.omitColumns;
  }

  return modifyTable;
};

export const mapSilverPresetsToDataSource = (
  datasource: core_v1_DataSource,
  preset?: content_v1_DatasourcePreset,
) => {
  const transforms = filterNulls(preset?.silver?.transform);

  return produce(
    datasource,
    (draftState: WritableDraft<core_v1_DataSource>) => {
      draftState.spec.silver = {
        ...draftState.spec.silver,
        transform: {
          ...draftState.spec.silver?.transform,
          skipSilverTransform: false,
          presetOverrides: {
            addTables: draftState.spec.silver?.transform?.presetOverrides
              ?.addTables?.length
              ? draftState.spec.silver?.transform?.presetOverrides?.addTables
              : undefined,
            modifyTables: transforms?.length
              ? transforms?.map((transform) =>
                  mapSilverModifyTablePresetToDatasource(transform, draftState),
                )
              : undefined,
            omitTables: draftState.spec.silver?.transform?.presetOverrides
              ?.omitTables?.length
              ? draftState.spec.silver?.transform?.presetOverrides?.omitTables
              : undefined,
          },
        },
      };
    },
  );
};

export const silverHasUnsavedChanges = (
  datasource: core_v1_DataSource,
  unsavedDatasource: core_v1_DataSource | undefined,
) => {
  if (!unsavedDatasource) {
    return false;
  }
  return !deepEqual(
    datasource.spec.silver?.transform,
    unsavedDatasource.spec.silver?.transform,
  );
};

export const mapSilverModifyTableToTransformRequest = (
  table: Datasource_Silver_ModifyTable,
): TransformRequest_Transform => {
  return {
    transformType: 'SilverTransform',
    usePresetTable: table?.name || undefined,
    filter: table?.filter || undefined,
    postFilter: table?.postFilter || undefined,
    utils: table?.utils || undefined,
    addFields: table?.addFields || undefined,
    presetOverrides: table?.omitFields
      ? { omitFields: table?.omitFields }
      : undefined,
  };
};

export const mapSilverAddTableToTransformRequest = (
  table: Datasource_Silver_AddTable,
): TransformRequest_Transform => {
  return {
    transformType: 'SilverTransform',
    usePresetTable: table?.name || undefined,
    filter: table?.filter || undefined,
    postFilter: table?.postFilter || undefined,
    utils: table?.utils || undefined,
    addFields: table?.fields || undefined,
  };
};

export const mapSilverTableToTransformRequest = (
  table: Datasource_Silver_AddTable,
  isModifyTable: boolean,
) => {
  if (isModifyTable) {
    return mapSilverModifyTableToTransformRequest(table);
  }
  return mapSilverAddTableToTransformRequest(table);
};
